import { useEffect, useState } from "react";
import z from "zod";
import { useSignupFormSchema } from "@/views/shared/useSignupFormSchema";

import AllEarsTitle from "@components/AllEarsTitle";

import { useParams } from "react-router-dom";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import Logo from "@components/ui/logo";
import { Button } from "@components/ui/button";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@components/ui/input-group";
import { EyeClosedIcon, EyeIcon } from "lucide-react";
import { Spinner } from "@components/ui/spinner";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import SignupImage from "@components/SignupImage";
import {
  PUBLIC_EN_SITE_DATA_POLICY,
  PUBLIC_EN_SITE_PRIVACY_POLICY,
  PUBLIC_EN_SITE_TERMS,
  ROUTES,
} from "@/routes_new";
import { useMutation, useQuery } from "@tanstack/react-query";
import { activate, validateActivationKey } from "@/api/endpoints/user";
import ErrorComponent from "@components/ui/error";
import { toast } from "sonner";
import { applyServerErrors } from "@/utils/forms";

export default function ActivationView() {
  const { t } = useLingui();

  const { activation_key } = useParams<{ activation_key: string }>();

  const { data: activationKeyData, isLoading: isActivationKeyLoading } =
    useQuery({
      queryKey: ["validate-activation-key", activation_key],
      queryFn: () => validateActivationKey(activation_key),
    });

  const formSchema = useSignupFormSchema({ requireTurnstile: false });

  const { setValue, ...form } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });

  const { mutateAsync: mutateActivate } = useMutation({
    mutationFn: activate,
    onSuccess: (res) => {
      if (res.ok) {
        window.location.href = ROUTES.APP_HOME;
        return;
      }

      if (res.status === 400 && res.data) {
        applyServerErrors(res.data, form, {
          first_name: "firstName",
          last_name: "lastName",
        });
        return;
      }

      throw new Error();
    },
    onError: () => {
      toast.error(t`Kunde inte acceptera inbjudan och skapa konto`);
    },
  });

  const [isShowingPassword, setIsShowingPassword] = useState<boolean>(false);

  useEffect(() => {
    if (activationKeyData?.ok && activationKeyData.data) {
      setValue("email", activationKeyData.data.email);
      setValue("firstName", activationKeyData.data.first_name);
      setValue("lastName", activationKeyData.data.last_name);
      if (activationKeyData.data.language) {
        i18n.activate(activationKeyData.data.language);
      }
    }
  }, [activationKeyData, setValue]);

  // Helpers
  const showSpinner = useDelayedLoading(form.formState.isSubmitting);

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = function (
    values
  ) {
    return mutateActivate({
      first_name: values.firstName,
      last_name: values.lastName,
      password: values.password,
      activation_key,
    });
  };

  return (
    <>
      <AllEarsTitle title={t`Acceptera din inbjudan`} />

      <div className="md:h-svh grid md:grid-cols-2">
        <div className="p-7 md:p-14 flex flex-col gap-[20svh] overflow-y-auto h-full">
          <Logo width={80} className="self-start" />

          {activationKeyData?.ok ? (
            <div className="w-full space-y-6 max-w-lg mx-auto">
              <div className="flex flex-col items-center text-balance text-center gap-1.5">
                <h1
                  className="text-2xl font-semibold"
                  data-testid="activation-heading"
                >
                  <Trans>Acceptera din inbjudan</Trans>
                </h1>
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    Kom igång med All Ears genom att fylla i dina uppgifter
                  </Trans>
                </p>
              </div>

              <form
                className="flex flex-col gap-6"
                onSubmit={form.handleSubmit(handleSubmit)}
              >
                <FieldGroup className="gap-5">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <Controller
                      control={form.control}
                      name="firstName"
                      render={({ field, fieldState }) => {
                        const id = "first-name";

                        return (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor={id}>
                              <Trans>Förnamn</Trans>
                            </FieldLabel>

                            <Input
                              {...field}
                              id={id}
                              autoComplete="given-name"
                              aria-invalid={fieldState.invalid}
                              placeholder={t`Jane`}
                            />

                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        );
                      }}
                    />

                    <Controller
                      control={form.control}
                      name="lastName"
                      render={({ field, fieldState }) => {
                        const id = "last-name";

                        return (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor={id}>
                              <Trans>Efternamn</Trans>
                            </FieldLabel>

                            <Input
                              {...field}
                              id={id}
                              autoComplete="family-name"
                              aria-invalid={fieldState.invalid}
                              placeholder={t`Smith`}
                            />

                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        );
                      }}
                    />
                  </div>

                  <Controller
                    control={form.control}
                    name="email"
                    render={({ field, fieldState }) => {
                      const id = "email";

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Din e-postadress för arbetet</Trans>
                          </FieldLabel>

                          <Input
                            {...field}
                            id={id}
                            disabled
                            readOnly
                            autoComplete="work email"
                            aria-invalid={fieldState.invalid}
                            placeholder={t`namn@foretag.com`}
                          />

                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    control={form.control}
                    name="password"
                    render={({ field, fieldState }) => {
                      const id = "password";

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Lösenord</Trans>
                          </FieldLabel>

                          <InputGroup>
                            <InputGroupInput
                              {...field}
                              id={id}
                              autoComplete="new-password"
                              placeholder="•••••••••••••"
                              aria-invalid={fieldState.invalid}
                              type={isShowingPassword ? "text" : "password"}
                            />

                            <InputGroupAddon align="inline-end">
                              <InputGroupButton
                                size="icon-xs"
                                type="button"
                                onClick={() => {
                                  setIsShowingPassword((prev) => !prev);
                                }}
                              >
                                {isShowingPassword ? (
                                  <EyeClosedIcon />
                                ) : (
                                  <EyeIcon />
                                )}
                              </InputGroupButton>
                            </InputGroupAddon>
                          </InputGroup>

                          {fieldState.invalid ? (
                            <FieldError errors={[fieldState.error]} />
                          ) : !field.value ? (
                            <FieldDescription>
                              <Trans>Minst 8 tecken</Trans>
                            </FieldDescription>
                          ) : null}
                        </Field>
                      );
                    }}
                  />
                </FieldGroup>

                <Button disabled={showSpinner.shouldRender} type="submit">
                  <Trans>Acceptera inbjudan och skapa konto</Trans>
                  {showSpinner.isVisible && <Spinner />}
                </Button>

                <p className="text-center text-sm text-muted-foreground text-balance">
                  <Trans>
                    Genom att fortsätta godkänner du våra{" "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={PUBLIC_EN_SITE_TERMS}
                      className="underline hover:text-foreground transition"
                    >
                      användarvillkor
                    </a>
                    , vår{" "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={PUBLIC_EN_SITE_PRIVACY_POLICY}
                      className="underline hover:text-foreground transition"
                    >
                      integritetspolicy
                    </a>{" "}
                    och vårt{" "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={PUBLIC_EN_SITE_DATA_POLICY}
                      className="underline hover:text-foreground transition"
                    >
                      personuppgiftsbiträdesavtal
                    </a>
                    .
                  </Trans>
                </p>
              </form>
            </div>
          ) : isActivationKeyLoading ? (
            <div className="w-full max-w-lg mx-auto flex justify-center">
              <Spinner />
            </div>
          ) : (
            <ErrorComponent
              data-testid="activation-error"
              heading={t`Ogiltig inbjudan`}
              className="justify-start"
              description={t`Tyvärr är inte den här inbjudan inte giltig. Be gärna din kollega att bjuda in dig på nytt!`}
            />
          )}
        </div>

        <SignupImage />
      </div>
    </>
  );
}
